import React, { useState } from "react";
import { CCButton, CCModal, CCSpinner } from "@pymblecitizencoders/cc-ui";
import { toDatePart, toTimePart } from "../../utils/eventDates";
import { EventDoc } from "../../types/event";
import "./eventManager.css";

interface EventSaveBarProps {
	event: EventDoc;
	saving: boolean;
	dirty: boolean;
	onSave: () => void;
	/** Throw the draft away and go back to the saved event. */
	onRevert: () => void;
}

/* Backwards ranges are not saved. The API takes whatever it is given, and an
   event ending before it starts is wrong in every consumer that reads it back.
   A date-only event allows the same day; one carrying a time needs the end
   strictly later. A half-filled range is not an error. */
const rangeInvalid = (event: EventDoc): boolean => {
	const startDate = toDatePart(event.StartDateTime);
	const endDate = toDatePart(event.EndDateTime);
	if (!startDate || !endDate) return false;
	const timed =
		(toTimePart(event.StartDateTime) || "00:00") !== "00:00" ||
		(toTimePart(event.EndDateTime) || "00:00") !== "00:00";
	if (!timed) return endDate < startDate;
	const from = new Date(event.StartDateTime ?? "");
	const to = new Date(event.EndDateTime ?? "");
	if (Number.isNaN(from.getTime()) || Number.isNaN(to.getTime())) return false;
	return to.getTime() <= from.getTime();
};

/**
 * Discard and Save changes, in the page header.
 *
 * Present but disabled until there is something to discard, so the pair keeps
 * its shape rather than reflowing under the pointer on the first keystroke.
 * Discard is NOT range-gated, unlike Save: an event whose dates are backwards
 * is exactly the state someone needs a way out of.
 *
 * Confirmed, because discarding is the one action here that destroys work and
 * cannot be undone: the draft is the only copy of what was typed.
 */
export const EventSaveBar: React.FC<EventSaveBarProps> = ({
	event,
	saving,
	dirty,
	onSave,
	onRevert,
}) => {
	const [confirmDiscard, setConfirmDiscard] = useState(false);
	return (
		<>
			<div className="em-save-bar">
				<CCButton
					variant="outline"
					className="em-side-discard"
					disabled={saving || !dirty}
					onClick={() => setConfirmDiscard(true)}
				>
					Discard
				</CCButton>
				<CCButton
					variant="solid"
					colour="primary"
					disabled={saving || !dirty || rangeInvalid(event)}
					onClick={onSave}
				>
					{saving ? (
						<CCSpinner size="inline" label="Saving..." />
					) : dirty ? (
						"Save changes"
					) : (
						"Saved"
					)}
				</CCButton>
			</div>

			<CCModal
				open={confirmDiscard}
				onClose={() => setConfirmDiscard(false)}
				title="Discard changes?"
				footer={
					<>
						<CCButton
							variant="outline"
							onClick={() => setConfirmDiscard(false)}
						>
							Keep editing
						</CCButton>
						<CCButton
							variant="solid"
							colour="danger"
							onClick={() => {
								setConfirmDiscard(false);
								onRevert();
							}}
						>
							Discard
						</CCButton>
					</>
				}
			>
				<p className="em-muted">
					Every unsaved edit on this event goes back to the last saved version.
					This cannot be undone.
				</p>
				<p className="em-muted">
					Attendee changes, requirement notes and anything already sent for
					sign-off are saved as they happen, so they are not affected.
				</p>
			</CCModal>
		</>
	);
};
